import type { AuthFetch } from "@p0security/frontend-core";
import { SINGLETON_ITEM } from "@p0security/shared/install/constants";
import type { ConfigOf, InstallSpec } from "@p0security/shared/install/types";
import { Typography } from "@p0security/ui";
import { pickBy } from "lodash";
import { useCallback, useMemo } from "react";

import type { FrontendInstallContext } from "../types";
import { configPath } from "./Config";
import { ConfigForm } from "./ConfigForm";
import type { InstallProps } from "./Install";

export type RootProps<T extends InstallSpec> = InstallProps<T> & {
  authFetch: AuthFetch;
  context: FrontendInstallContext<ConfigOf<T>>;
  disabled?: boolean;
  isFetching: boolean;
};

export const Root = <T extends InstallSpec>(props: RootProps<T>) => {
  const { authFetch, context, isFetching, registration } = props;

  const component = registration.components.root;

  const submit = useCallback(
    async (config: ConfigOf<T>["root"]) => {
      await authFetch(
        configPath({
          integration: props.integration,
          componentKey: undefined,
          id: undefined,
        }),
        {
          method: "POST",
          json: { root: { [SINGLETON_ITEM]: config } },
        }
      );
    },
    [authFetch, props.integration]
  );
  const onSubmit = useCallback(
    (config: ConfigOf<T>["root"]) => void submit(config),
    [submit]
  );

  const schema = useMemo(
    () => pickBy(component.schema, (s: any) => s.type !== "generated"),
    [component.schema]
  );

  const config = useMemo(
    () => props.context.config?.root?.[SINGLETON_ITEM] ?? {},
    [props.context]
  );

  return (
    <div>
      {!props.disabled && (
        <Typography.Paragraph>
          To begin, configure your integration, then click &quot;Next&quot;:
        </Typography.Paragraph>
      )}
      <ConfigForm
        config={config}
        context={context}
        allDisabled={props.disabled}
        id={SINGLETON_ITEM}
        installer={props.installer.components.root as any}
        isFetching={isFetching}
        item={config}
        schema={schema}
        setConfig={onSubmit}
        step="configure"
      />
    </div>
  );
};
